<script setup lang="ts">
import { Clock } from 'lucide-vue-next';
import type { CommonFooterFullProperties, FooterLinksList } from '@optionway/models';

const properties = defineProps<CommonFooterFullProperties>();
const { t } = useI18n();
const prefix = 'components.common.footer';

const { renew } = useCookieConsent();

const { $appConfig } = useNuxtApp();
const config = $appConfig.app?.components?.commonFooterFull;

const componentConfig = computed<CommonFooterFullProperties>(() => {
  return {
    logoName: properties.logoName ?? config?.logoName ?? 'optionway',
    helpUrl: properties.helpUrl ?? config?.helpUrl,
  };
});

const footerLinks: FooterLinksList[] = [
  {
    title: `${prefix}.help`,
    links: [
      {
        type: 'link',
        label: `${prefix}.faq`,
        to: useLocalized(componentConfig.value.helpUrl).value,
        icon: 'ExternalLink',
        target: '_blank',
        title: `${t('components.common.header.help')} (${t('components.common.newWindow')})`,
      },
      {
        type: 'link',
        label: `${prefix}.contact`,
        to: '#',
        target: '_blank',
      },
      {
        type: 'link',
        label: `${prefix}.complaint`,
        to: '#',
        target: '_blank',
      },
    ],
    text: `${prefix}.hours`,
  },
  {
    title: `${prefix}.about`,
    links: [
      {
        type: 'multi-link',
        items: [
          {
            type: 'link',
            label: `${prefix}.terms`,
            to: 'terms',
            target: '_blank',
            localeAware: true,
          },
          {
            type: 'text',
            text: `${prefix}.and`,
          },
          {
            type: 'link',
            label: `${prefix}.legalNotice`,
            to: 'legal-notices',
            target: '_blank',
            localeAware: true,
          },
        ],
      },
      {
        type: 'link',
        label: `${prefix}.privacyPolicy`,
        to: 'privacy-policy',
        target: '_blank',
        localeAware: true,
      },
      {
        type: 'link',
        label: `${prefix}.cookiesPreferences`,
        action: renew,
      },
      {
        type: 'link',
        label: `${prefix}.accessibilityNonCompliant`,
        to: 'accessibility',
        target: '_blank',
        localeAware: true,
      },
    ],
  },
];
</script>

<template>
  <div class="flex flex-col gap-8 md:grid md:grid-cols-3 lg:grid-cols-footer lg:gap-20">
    <div class="flex h-fit flex-col gap-4">
      <component
        :is="`SvgoCommonFooterBranded${useUpperFirst(componentConfig.logoName!)}`"
        filled
        aria-describedby="common-footer-ow-description"
        width="139"
        height="29"
        :font-controlled="false"
      />

      <p
        id="common-footer-ow-description"
        class="text-balance text-sm font-normal text-secondary-foreground"
      >
        {{ $t(`${prefix}.description`) }}
      </p>

      <CommonFooterStarsRating />
    </div>

    <div
      v-for="footerLinkList in footerLinks"
      :key="footerLinkList.title"
      class="flex flex-col gap-3"
    >
      <span class="text-xs font-semibold uppercase tracking-wider text-text-accent-700">
        {{ $t(footerLinkList.title) }}
      </span>

      <ul class="flex flex-col gap-2">
        <li
          v-for="(link, index) in footerLinkList.links"
          :key="`${footerLinkList.title}-${index}`"
          class="text-secondary-foreground"
        >
          <template v-if="link.type === 'link'">
            <button
              v-if="link.action"
              type="button"
              class="inline-flex cursor-pointer items-center gap-2 text-left text-sm font-normal hover:text-text-accent-700"
              @click="link.action()"
            >
              {{ $t(link.label) }}
              <component
                :is="resolveLucideIcon(link.icon)"
                v-if="link.icon"
                class="size-4 shrink-0 text-text-secondary-600"
                aria-hidden="true"
              />
            </button>
            <NuxtLinkLocale
              v-else-if="link.localeAware"
              :to="link.to"
              :target="link.target"
              :title="link.title"
              class="inline-flex cursor-pointer items-center gap-2 text-sm font-normal hover:text-text-accent-700"
            >
              {{ $t(link.label) }}
              <component
                :is="resolveLucideIcon(link.icon)"
                v-if="link.icon"
                class="size-4 shrink-0 text-text-secondary-600"
                aria-hidden="true"
              />
            </NuxtLinkLocale>
            <NuxtLink
              v-else
              :to="link.to"
              :target="link.target"
              :title="link.title"
              class="inline-flex cursor-pointer items-center gap-2 text-sm font-normal hover:text-text-accent-700"
            >
              {{ $t(link.label) }}
              <component
                :is="resolveLucideIcon(link.icon)"
                v-if="link.icon"
                class="size-4 shrink-0 text-text-secondary-600"
                aria-hidden="true"
              />
            </NuxtLink>
          </template>

          <CommonFooterMultiLink
            v-else-if="link.type === 'multi-link'"
            :multi-link="link"
          />
        </li>
      </ul>
      <div
        v-if="footerLinkList.text"
        class="flex items-center gap-1.5 text-sm font-normal text-text-secondary-600"
      >
        <Clock
          class="size-3.5 shrink-0 text-text-secondary-600"
          :stroke-width="2"
          aria-hidden="true"
        />
        {{ $t(footerLinkList.text) }}
      </div>
    </div>
  </div>
</template>
